// Tabs: section tabs on the SegTrack well. One gold-tinted thumb springs to the open section;
// arrow keys, Home and End move between tabs and open them. A tab can carry a completion ring
// (the event page's sections, `fill` + `size="lg"`), a mark for unsaved edits, a count or a
// badge. TabPanel holds the open section and fades it in on each switch.
// docs/operations/design-standard.md, "Controls".

import React, { useState } from 'react';
import CompletionRing from '../CompletionRing';
import { ringLabel } from './ProgressRing';
import Appear from './Appear';
import FadeIn from './FadeIn';
import Tooltip from './Tooltip';
import { SegKeyBody, SegKeyContent, SegSize, SegThumb, segFillStyle, segKeyClass, segTrackClass, useSegThumb } from './SegTrack';

export interface TabRing {
  filled: number;
  total: number;
  locked?: number;
  attention?: boolean;
}

export interface TabItem extends SegKeyContent {
  key: string;
  /** How complete the section is, drawn before the label and spoken after it. */
  ring?: TabRing;
  /** The section holds edits not yet saved: a dot in the key's corner, spoken as "unsaved changes". */
  dirty?: boolean;
}

export interface TabsProps {
  /** Names the tab list: "Event sections". */
  label: string;
  tabs: TabItem[];
  value: string;
  onChange: (key: string) => void;
  /** Ties each tab to its TabPanel. Give the same id to both. */
  id?: string;
  size?: SegSize;
  /** Equal-width tabs filling the row: the section strip at the top of a record. */
  fill?: boolean;
  /** Tabs flow onto a second row when out of width, instead of truncating. */
  wrap?: boolean;
  className?: string;
}

const RING_SIZE: Record<SegSize, number> = { sm: 12, md: 14, lg: 16 };

export const tabDomId = (id: string, key: string): string => `${id}-tab-${key}`;
export const panelDomId = (id: string, key: string): string => `${id}-panel-${key}`;

let seq = 0;

const NAV_KEYS = new Set(['ArrowRight', 'ArrowLeft', 'Home', 'End']);

export const Tabs: React.FC<TabsProps> = ({ label, tabs, value, onChange, id, size = 'md', fill = false, wrap = false, className = '' }) => {
  const [auto] = useState(() => `ui-tabs-${++seq}`);
  const base = id || auto;
  const active = tabs.findIndex((t) => t.key === value);
  const { track, keys, thumb } = useSegThumb(active, tabs.length);
  // One tab in the page's tab order (roving): the open one, or the first that can open.
  const entry = active >= 0 && !tabs[active].disabled ? active : tabs.findIndex((t) => !t.disabled);

  const step = (from: number, e: React.KeyboardEvent) => {
    if (!NAV_KEYS.has(e.key)) return;
    // Disabled tabs stay in the path so a keyboard user reaches their hint; they take focus but never open.
    const at = from;
    let next = 0;
    if (e.key === 'End') next = tabs.length - 1;
    else if (e.key === 'ArrowRight') next = (at + 1) % tabs.length;
    else if (e.key === 'ArrowLeft') next = at <= 0 ? tabs.length - 1 : at - 1;
    e.preventDefault();
    keys.current[next]?.focus();
    if (!tabs[next].disabled && tabs[next].key !== value) onChange(tabs[next].key);
  };

  return (
    <div
      ref={track}
      role="tablist"
      aria-label={label}
      className={`${segTrackClass(fill, wrap)} ${className}`}
      style={segFillStyle(fill, tabs.length)}
    >
      <SegThumb {...thumb} />
      {tabs.map((t, i) => {
        const on = i === active;
        const said = [
          t.ring ? ringLabel(t.ring.filled, t.ring.total, t.ring.locked, t.ring.attention) : '',
          t.dirty ? 'unsaved changes' : '',
        ].filter(Boolean);
        const btn = (
          <button
            key={t.key}
            ref={(b) => { keys.current[i] = b; }}
            type="button"
            role="tab"
            id={tabDomId(base, t.key)}
            aria-selected={on}
            aria-controls={id ? panelDomId(id, t.key) : undefined}
            aria-disabled={t.disabled || undefined}
            aria-label={t.aria ? [t.aria, ...said].join(', ') : undefined}
            tabIndex={i === entry ? 0 : -1}
            onClick={() => { if (!t.disabled && !on) onChange(t.key); }}
            onKeyDown={(e) => step(i, e)}
            className={segKeyClass(size, on, { disabled: t.disabled })}
          >
            <SegKeyBody
              o={t}
              stack={fill && !!(t.ring || t.icon)}
              lead={t.ring ? (
                <span aria-hidden="true" className="inline-flex flex-none">
                  <CompletionRing {...t.ring} size={RING_SIZE[size]} />
                </span>
              ) : undefined}
            />
            {said.length > 0 && !t.aria && <span className="sr-only">{said.map((s) => `, ${s}`).join('')}</span>}
            <Appear show={!!t.dirty} className="pointer-events-none absolute right-1 top-1 h-1.5 w-1.5 rounded-full bg-primary" />
          </button>
        );
        return t.hint ? <Tooltip key={t.key} label={t.hint}>{btn}</Tooltip> : btn;
      })}
    </div>
  );
};

export interface TabPanelProps {
  /** The id given to Tabs. */
  id: string;
  /** The open tab's key. */
  value: string;
  className?: string;
  children: React.ReactNode;
}

/** The open section, labelled by its tab. Keyed by the tab, so each switch fades the new one in. */
export const TabPanel: React.FC<TabPanelProps> = ({ id, value, className = '', children }) => (
  <div role="tabpanel" id={panelDomId(id, value)} aria-labelledby={tabDomId(id, value)}>
    <FadeIn key={value} className={className}>{children}</FadeIn>
  </div>
);

export default Tabs;
